/**
 * Whatsapp Lead — top-level sidebar group for WhatsApp CRM screens.
 *
 * Replaces the single "WhatsApp CRM" entry that lived under Admin. Has four
 * sub-pages, each a deep-link into /admin/whatsapp-crm with a query param
 * the page reads to auto-select the right tab:
 *   - Assign Leads          ?tab=assign
 *   - My Chats              (/dashboard/my-chats — agent's personal inbox)
 *   - Allocation Health     ?tab=health
 *   - Organic vs FB         ?tab=orgvsfb
 *
 * Visibility:
 *   - Group itself shows for role 1 (admin/owner), role 17 (Ameen / retail
 *     super-manager), and admins flagged bulkWaManager=true (Manjay / bulk
 *     super-manager).
 *   - "My Chats" additionally surfaces for any agent where admins.whatsappAgent
 *     === true (it's their own inbox, not a supervisor view).
 */

import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  IconMessages,
  IconMessageCircle,
  IconChartBar,
  IconFlag,
  IconBan,
  IconNotes,
  IconCalendarStats,
  IconTarget,
  IconRobot,
} from "@tabler/icons";
import { getDecryptDataFromLocal } from "../../utils/encrypt";
import waPendingCount from "../../Services/waPendingCount";
import { canAccessPinnacleChats } from "./pinnacleChatsAccess";

const icons = { IconMessages, IconMessageCircle, IconChartBar, IconFlag, IconBan, IconNotes, IconCalendarStats, IconTarget, IconRobot };

// Red notification badge on sidebar titles — shows the WhatsApp pending
// count (buyer-last-message OR last-outbound-was-auto-reply). Subscribes to
// the singleton service so a single backend poll drives all instances.
// Hidden when count is 0 so the sidebar stays clean.
const PendingBadge = () => {
  const [n, setN] = useState(waPendingCount.current);
  useEffect(() => waPendingCount.subscribe(setN), []);
  if (!n || n <= 0) return null;
  const label = String(n);
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      marginLeft: 8,
      borderRadius: 10,
      background: "#d32f2f",
      color: "#fff",
      fontSize: 11,
      fontWeight: 800,
      lineHeight: 1,
    }}>{label}</span>
  );
};

// Wrap a label with the badge inline. Used in both the group title and the
// My Chats child item, so they show the same live number.
const withBadge = (id) => (
  <span style={{ display: "inline-flex", alignItems: "center" }}>
    <FormattedMessage id={id} />
    <PendingBadge />
  </span>
);

const role               = Number(getDecryptDataFromLocal("setRole"));
const isWhatsAppAgent    = (() => { try { return localStorage.getItem("whatsappAgent") === "true"; } catch (e) { return false; } })();
const isBulkWaManager    = (() => { try { return localStorage.getItem("bulkWaManager") === "true"; } catch (e) { return false; } })();
const isPersonalUseMgr   = (() => { try { return localStorage.getItem("personalUseManager") === "true"; } catch (e) { return false; } })();
const isSupervisor       = role === 1 || role === 17 || isBulkWaManager;
// Ravi Shankar Kumar Singh + Vivek Patel get Assign Leads for Diaper Leads
// only (voiz-number leads, Shailendra 2026-10-03). Mirrors
// DIAPER_LEAD_VIEWER_IDS in manijment-api voizNumberLock.js.
const isDiaperViewer     = (() => { try { return ['61a5c97e996886bb19ee8cda', '6a586b3990fc721729d649a2'].includes(localStorage.getItem("adminId")); } catch (e) { return false; } })();
// Cohort floor managers (Abid/Ravi/Mirza/OM/Ankit etc.) get visibility into
// the WhatsApp section scoped to their own agents. Backend forces managerId
// to viewer's _id on /whatsapp/agents + /whatsapp/agent-stats, so they can't
// see other managers' teams even if a request is hand-crafted.
const isCohortManager  = role === 13 || role === 14;

const children = [];

// Assign Leads is the only sub-item kept supervisor-only — cohort managers
// don't allocate new leads, they just review their team. Diaper viewers
// (Ravi / Vivek Patel) also get it, scoped to Diaper Leads by the page.
if (isSupervisor || isDiaperViewer) {
  children.push({
    id: "wa-assign-leads",
    title: <FormattedMessage id="Assign Leads" />,
    type: "item",
    url: "/admin/whatsapp-crm/assign",
    icon: icons.IconMessages,
    breadcrumbs: false,
  });
}

// AI Conversion + AI Enabler — how the AI BOT itself is performing across the
// whole floor. That's an owner-level view, not a per-team operational one, so
// these are supervisor-only: role 1 (admin/owner), role 17 (Ameen), bulk WA
// manager. Deliberately NOT shown to floor managers/TLs (role 13/14 — Mirza,
// OM and the rest), who previously saw them via the isCohortManager branch.
// Shailendra 2026-08-22.
if (isSupervisor) {
  children.push({
    id: "wa-ai-conversion",
    title: <FormattedMessage id="AI Conversion" defaultMessage="AI Conversion" />,
    type: "item",
    url: "/admin/whatsapp-crm/ai-conversion",
    icon: icons.IconRobot,
    breadcrumbs: false,
  });
  children.push({
    id: "wa-ai-enabler",
    title: <FormattedMessage id="AI Enabler" defaultMessage="AI Enabler" />,
    type: "item",
    url: "/admin/whatsapp-crm/ai-enabler",
    icon: icons.IconRobot,
    breadcrumbs: false,
  });
  // Bolna voice qualifier test bench. Supervisors only — this page PLACES
  // REAL CALLS and spends money, so it is not something an agent should
  // reach. The qualifier is not wired to lead intake; this page is the only
  // way a call gets placed while QUALIFIER_TEST_MODE=1 (Shailendra
  // 2026-10-20).
  children.push({
    id: "wa-ai-qualifier-test",
    title: <FormattedMessage id="AI Call Qualifier" defaultMessage="AI Call Qualifier" />,
    type: "item",
    url: "/voice/ai-qualifier-test",
    icon: icons.IconRobot,
    breadcrumbs: false,
  });
}

// Call Attendance report — daily P/H team table + the calls-per-lead & pool-
// coverage conversion drivers. For supervisors and cohort managers/TLs (they
// run the floor). Agents see their own card on the dashboard, not this page.
if (isSupervisor || isCohortManager) {
  children.push({
    id: "wa-call-attendance",
    title: <FormattedMessage id="Call Attendance" defaultMessage="Call Attendance" />,
    type: "item",
    url: "/admin/whatsapp-crm/call-attendance",
    icon: icons.IconCalendarStats,
    breadcrumbs: false,
  });
  // Dialer Plan — download the progressive-dialer daily allocation (dry-run) to
  // verify before go-live. Supervisor/manager only.
  children.push({
    id: "wa-dialer-plan",
    title: <FormattedMessage id="Dialer Plan" defaultMessage="Dialer Plan" />,
    type: "item",
    url: "/admin/whatsapp-crm/dialer-plan",
    icon: icons.IconChartBar,
    breadcrumbs: false,
  });
}

if (isPersonalUseMgr) {
  children.push({
    id: "wa-personal-used",
    title: <FormattedMessage id="Personal Used" />,
    type: "item",
    url: "/dashboard/my-chats",
    icon: icons.IconMessageCircle,
    breadcrumbs: false,
  });
} else if (isWhatsAppAgent || isSupervisor || isCohortManager) {
  children.push({
    id: "wa-my-chats",
    title: withBadge("My Chats"),
    type: "item",
    url: "/dashboard/my-chats",
    icon: icons.IconMessageCircle,
    breadcrumbs: false,
  });
}

// AI Chats — supervisor-only view of conversations handled by Shahnawaz's
// AI auto-reply agent (admin _id 6358f5ad91c4f84f35ebcbff). Pinned to that
// one inbox so we can review what the AI said to each buyer end-to-end.
// Bulk team (Manjay) is excluded — AI only handles retail leads.
if (isSupervisor && !isBulkWaManager) {
  children.push({
    id: "wa-ai-chats",
    title: <FormattedMessage id="AI Chats" />,
    type: "item",
    url: "/dashboard/ai-chats",
    icon: icons.IconMessageCircle,
    breadcrumbs: false,
  });
}

// Missed Call Leads moved to its own top-level section
// (see menu-items/MissedCallLeads/index.js). Removed from here 2026-06-29.

// All Conversations (First Message Report) — Admin (role 1) + Ameen (role
// 17) + Manjay (bulkWaManager) get the full unrestricted compliance/audit
// report across every WhatsApp conversation. Cohort floor managers (13/14)
// also get this tab now, but scoped server-side to only the leads CURRENTLY
// assigned to their own agents — see resolveAllConversationsAccess.
if (isSupervisor || isCohortManager) {
  children.push({
    id: "wa-all-conversations",
    title: <FormattedMessage id="All Conversations" />,
    type: "item",
    url: "/admin/whatsapp-crm/all-conversations",
    icon: icons.IconNotes,
    breadcrumbs: false,
  });
}

if (isSupervisor || isCohortManager) {
  children.push({
    id: "wa-not-interested",
    title: <FormattedMessage id="Not Interested" />,
    type: "item",
    url: "/admin/whatsapp-crm/not-interested",
    icon: icons.IconBan,
    breadcrumbs: false,
  });
  children.push({
    id: "wa-lead-profiles",
    title: <FormattedMessage id="Lead Profiles" />,
    type: "item",
    url: "/admin/whatsapp-crm/lead-profiles",
    icon: icons.IconNotes,
    breadcrumbs: false,
  });
  children.push({
    id: "wa-allocation-health",
    title: <FormattedMessage id="Allocation Health" />,
    type: "item",
    url: "/admin/whatsapp-crm/health",
    icon: icons.IconFlag,
    breadcrumbs: false,
  });
  children.push({
    id: "wa-organic-vs-fb",
    title: <FormattedMessage id="Organic vs FB" />,
    type: "item",
    url: "/admin/whatsapp-crm/orgvsfb",
    icon: icons.IconChartBar,
    breadcrumbs: false,
  });
  children.push({
    id: "wa-day-wise-conversion",
    title: <FormattedMessage id="Day Wise Conversion" />,
    type: "item",
    url: "/admin/whatsapp-crm/day-wise-conversion",
    icon: icons.IconCalendarStats,
    breadcrumbs: false,
  });
  children.push({
    id: "wa-b2b-leads-roi",
    title: <FormattedMessage id="B2B Leads ROI" defaultMessage="B2B Leads ROI" />,
    type: "item",
    url: "/admin/whatsapp-crm/ai-conversion",
    icon: icons.IconRobot,
    breadcrumbs: false,
  });
  children.push({
    id: "wa-ai-enabler",
    title: <FormattedMessage id="AI Enabler" defaultMessage="AI Enabler" />,
    type: "item",
    url: "/admin/whatsapp-crm/ai-enabler",
    icon: icons.IconRobot,
    breadcrumbs: false,
  });
  // B2B Leads ROI hidden from team leads (13) and plain agents (5) per
  // Shailendra 2026-08-04 — role 5 was already excluded structurally (neither
  // isSupervisor nor isCohortManager is true for them), role 13 is carved out
  // explicitly here since isCohortManager covers both 13 and 14.
  if (role !== 13 && role !== 5) {
    children.push({
      id: "wa-b2b-leads-roi",
      title: <FormattedMessage id="B2B Leads ROI" defaultMessage="B2B Leads ROI" />,
      type: "item",
      url: "/admin/whatsapp-crm/b2b-leads-roi",
      icon: icons.IconTarget,
      breadcrumbs: false,
    });
  }
}

// Order Report — download tab (numericOrderId, agent/manager, sellerId,
// isWaLeadOrder/waSource, date filter). Admin (1) + ops/finance/inventory
// (2,3,4) see all; Ameen (17) + cohort managers (13,14) see own hierarchy;
// Manjay (bulkWaManager) sees bulk team only — all via getBulkReportAgentFilter
// on the backend. Plain agents (role 5) never get this tab.
if ([1, 2, 3, 4].includes(role) || isSupervisor || isCohortManager) {
  children.push({
    id: "wa-order-report",
    title: <FormattedMessage id="Order Report" defaultMessage="Order Report" />,
    type: "item",
    url: "/admin/whatsapp-crm/order-report",
    icon: icons.IconNotes,
    breadcrumbs: false,
  });
}

// Agent Wise Lead Report — download tab, row-per-lead detail (agent identity +
// lead identity + assignment dates + ad attribution). Sits between Order
// Report and Agent Lead Report. Locked tighter than every other WA report
// here: ONLY admin (role 1), Ameen (role 17), and Manjay (bulkWaManager) —
// no ops/finance/inventory, no team lead/floor manager. Backend re-enforces
// the same check. Shailendra 2026-08-14.
if (role === 1 || role === 17 || isBulkWaManager) {
  children.push({
    id: "wa-agent-wise-lead-report",
    title: <FormattedMessage id="Agent Wise Lead Report" defaultMessage="Agent Wise Lead Report" />,
    type: "item",
    url: "/admin/whatsapp-crm/agent-wise-lead-report",
    icon: icons.IconNotes,
    breadcrumbs: false,
  });
}

// Agent Month-Wise Lead Report — download tab. Same visibility rule as Order
// Report above: Admin (1) + ops/finance/inventory (2,3,4) see all agents;
// Ameen (17) + cohort managers (13,14) see their own hierarchy; Manjay
// (bulkWaManager) sees the bulk team only — all enforced server-side by
// downloadAgentMonthWiseLeadReport. Plain agents (role 5) never see this tab.
if ([1, 2, 3, 4].includes(role) || isSupervisor || isCohortManager) {
  children.push({
    id: "wa-agent-lead-report",
    title: <FormattedMessage id="Agent Lead Report" defaultMessage="Agent Lead Report" />,
    type: "item",
    url: "/admin/whatsapp-crm/agent-lead-report",
    icon: icons.IconCalendarStats,
    breadcrumbs: false,
  });
}

// Pinnacle Chats — threads on the Pinnacle (PinBot) bulk-campaign number
// +91 92173 37974: every buyer we messaged from the PinBot dashboard, their
// replies, and a reply box (24h window). Kept separate from My Chats. Admins
// (role 1) + 9268096291 only; backend enforces the same list on /pinnacle-wa/*.
if (canAccessPinnacleChats()) {
  children.push({
    id: "wa-pinnacle-chats",
    title: <FormattedMessage id="Pinnacle Chats" defaultMessage="Pinnacle Chats" />,
    type: "item",
    url: "/admin/whatsapp-crm/pinnacle-chats",
    icon: icons.IconMessageCircle,
    breadcrumbs: false,
  });
}

// Bulk Reassign — owner (role 1) + Ameen (role 17) across retail; managers /
// team leads (role 13/14) within their own team. Backend enforces the same
// scope (agent list via agent-stats viewerId, reassign via downline check).
if (role === 1 || role === 17 || isCohortManager) {
  children.push({
    id: "wa-bulk-reassign",
    title: <FormattedMessage id="Bulk Reassign" />,
    type: "item",
    url: "/admin/whatsapp-crm/bulk-reassign",
    icon: icons.IconMessages,
    breadcrumbs: false,
  });
}

export const WhatsappLeadTab = {
  id: "whatsapp-lead",
  title: <FormattedMessage id="Whatsapp Lead" />,
  type: "group",
  children: children.length > 0 ? [
    {
      id: "wa-group-root",
      title: withBadge("Whatsapp Lead"),
      type: "collapse",
      icon: icons.IconMessages,
      children,
    },
  ] : [],
};

// Convenience boolean re-exported so menu-items/index.js can decide whether
// to even include this group for the current user.
// Roles 2,3,4 (ops/finance/inventory) added so the Order Report tab is
// reachable for them even without any of the other WA-specific flags.
export const SHOULD_SHOW_WHATSAPP_LEAD = isWhatsAppAgent || isSupervisor || isCohortManager || isPersonalUseMgr || isDiaperViewer || [2, 3, 4].includes(role) || canAccessPinnacleChats();
